/**
 * CustomerDisposalFeesPage — what the company CHARGES each customer per barrel to dispose of
 * its water at each place (#52, DEC-358). Replaces the legacy "Destinations Xref" grid.
 *
 * Each place has one DEFAULT fee; a customer gets its own price only where it differs. Three
 * tabs:
 *  - By place: every place, its default, how many customers pay something else, and (for
 *    price managers) what the place costs us. Opening a place shows its customer prices,
 *    "set for all customers", and its history. "Seed defaults from our cost" fills empty
 *    defaults from the disposal prices page.
 *  - By customer: every place for one customer, the fee that applies and where it comes
 *    from; "one fee at every place" and "copy prices from another customer".
 *  - Export & import: the long list as CSV or Excel, and uploading it back.
 *
 * Every bulk change and every import is previewed, applied all-or-nothing and audited
 * (FeeChangePreview). Prices are never edited in place: a change starts on a date, and the
 * old price stays in the history. Viewing needs VIEW_INVOICES; changing needs
 * MANAGE_DISPOSAL_PRICES — `canEdit` mirrors that for the UI only; the API enforces it.
 */

import React from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { apiErrorText, useApiOpts } from '@lodex-pro/web-shared';
import { TAB_TRACK_STYLE, tabPillStyle } from 'src/components/ui/tab-styles';
import { Button } from 'src/components/ui/button';
import { Input } from 'src/components/ui/input';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from 'src/components/ui/table';
import { SortableHead } from 'src/components/ui/sortable-head';
import { sortRows, useUrlSort } from '../lib/url-state';
import {
  SOURCE_LABELS,
  copyCustomerRows,
  costToCents,
  formatFee,
  oneFeeEverywhereRows,
  readFeeInput,
  seedFromCostRows,
  setForAllRows,
  type Customer,
  type FeeSite,
  type PlaceKind,
} from '../features/billing/customer-disposal-fees';
import {
  downloadFees,
  parseFeeFile,
  useCustomerFees,
  useFeeCustomers,
  useFeeHistory,
  useFeeSite,
  useFeeSites,
  useSetFee,
  type HistoryFilter,
  type PreviewRequest,
} from '../features/billing/customer-disposal-fees-hooks';
import { FeeChangePreview } from '../features/billing/FeeChangePreview';
import { formatNumber } from '@lodex-pro/shared/number-format';

const helpStyle: React.CSSProperties = { fontSize: '13px', color: 'var(--color-text-secondary)' };
const labelStyle: React.CSSProperties = { display: 'block', fontWeight: 600, marginBottom: '6px' };
const selectStyle: React.CSSProperties = {
  minHeight: '38px',
  padding: '8px 10px',
  fontSize: '14px',
  backgroundColor: 'var(--color-bg-tertiary)',
  border: '1px solid var(--color-border-secondary)',
  borderRadius: '10px',
  color: 'var(--color-text-primary)',
};
const boxStyle: React.CSSProperties = {
  borderRadius: 'var(--radius-card)',
  backgroundColor: 'var(--color-bg-secondary)',
  padding: '16px',
  marginBottom: '20px',
};

type Tab = 'place' | 'customer' | 'file';
type Place = { readonly kind: PlaceKind; readonly id: string };

/**
 * Deep link (#52 PR 3's In Review "Set fee now"):
 *   /disposal-fees?tab=customer&customerId=<haulerOperatorId>&kind=FACILITY|LOCATION&placeId=<id>
 * opens By customer on that customer with the fee form for that place (price managers only;
 * others just see the customer's fees). Any missing or malformed part is ignored.
 */
function readDeepLink(params: URLSearchParams): {
  readonly tab: Tab;
  readonly customerId: string | null;
  readonly place: Place | null;
} {
  const tab = params.get('tab');
  const customerId = params.get('customerId');
  const kind = params.get('kind');
  const placeId = params.get('placeId');
  const t: Tab = tab === 'customer' || tab === 'file' ? tab : 'place';
  return {
    tab: t,
    customerId: t === 'customer' && customerId !== null && customerId !== '' ? customerId : null,
    place:
      t === 'customer' &&
      (kind === 'FACILITY' || kind === 'LOCATION' || kind === 'WELL') &&
      placeId !== null &&
      placeId !== ''
        ? { kind, id: placeId }
        : null,
  };
}

export function CustomerDisposalFeesPage(props: { readonly canEdit: boolean }): React.JSX.Element {
  const [params] = useSearchParams();
  const link = React.useMemo(() => readDeepLink(params), [params]);
  const [tab, setTab] = React.useState<Tab>(link.tab);
  const sites = useFeeSites();
  const today = sites.data?.today ?? '';

  return (
    <div style={{ padding: '24px', maxWidth: '1000px' }}>
      <p style={{ ...helpStyle, marginBottom: '16px' }}>
        What you <strong>charge</strong> each customer per barrel to dispose of their water. Each
        place has a default fee; give a customer its own price only where it is different. Invoices
        name the place by its catalog name. (What disposal places charge <em>you</em> is on the
        Disposal prices page.)
      </p>
      <div role="tablist" style={{ ...TAB_TRACK_STYLE, marginBottom: '20px' }}>
        {(
          [
            ['place', 'By place'],
            ['customer', 'By customer'],
            ['file', 'Export & import'],
          ] as const
        ).map(([id, label]) => (
          <Button
            key={id}
            role="tab"
            aria-selected={tab === id}
            variant="ghost"
            style={tabPillStyle(tab === id)}
            onClick={(): void => setTab(id)}
          >
            {label}
          </Button>
        ))}
      </div>
      {sites.isError && (
        <p role="alert" style={{ color: 'var(--color-danger)' }}>
          Couldn&apos;t load the disposal fees.
        </p>
      )}
      {sites.isLoading && (
        <p role="status" style={helpStyle}>
          Loading…
        </p>
      )}
      {sites.data !== undefined && tab === 'place' && (
        <ByPlace sites={sites.data.sites} today={today} canEdit={props.canEdit} />
      )}
      {sites.data !== undefined && tab === 'customer' && (
        <ByCustomer
          today={today}
          canEdit={props.canEdit}
          initialCustomerId={link.customerId}
          initialPlace={link.place}
        />
      )}
      {sites.data !== undefined && tab === 'file' && (
        <ExportImport today={today} canEdit={props.canEdit} />
      )}
    </div>
  );
}

// ── By place ───────────────────────────────────────────────────────────────────────

/** The columns the By place table sorts by (OPEN-204), as ?sort=&dir=. */
const PLACE_SORTS = ['place', 'defaultFee', 'customerPrices', 'cost', 'margin'] as const;
/** The place detail's customer prices (OPEN-204), as ?prices=&pricesDir=. */
const PLACE_PRICE_SORTS = ['customer', 'fee'] as const;
/** The By customer table (OPEN-204), as ?fees=&feesDir=. */
const CUSTOMER_FEE_SORTS = ['place', 'fee', 'source', 'since'] as const;

function ByPlace(props: {
  readonly sites: FeeSite[];
  readonly today: string;
  readonly canEdit: boolean;
}): React.JSX.Element {
  const [open, setOpen] = React.useState<Place | null>(null);
  const [bulk, setBulk] = React.useState<{ title: string; request: PreviewRequest } | null>(null);
  const seesCost = props.sites.some((s) => s.costHundredthsCentPerBbl !== undefined);
  const seedRows = seedFromCostRows(props.sites);
  // OPEN-204 — click a header to sort; every place is loaded, so this sorts them all.
  const { sort, toggle } = useUrlSort(PLACE_SORTS, null, []);
  const sites = sortRows(props.sites, sort, (s, k) => {
    const cost = s.costHundredthsCentPerBbl ?? null;
    const fee = s.defaultFee?.feeCentsPerBbl ?? null;
    switch (k) {
      case 'place':
        return s.name;
      case 'defaultFee':
        return fee;
      case 'customerPrices':
        return s.customerPriceCount;
      case 'cost':
        return cost;
      case 'margin':
        return cost === null || fee === null ? null : fee - costToCents(cost);
    }
  });

  return (
    <>
      {props.canEdit && seedRows.length > 0 && bulk === null && (
        <div style={{ ...boxStyle, display: 'flex', gap: '12px', alignItems: 'center' }}>
          <span style={helpStyle}>
            {String(seedRows.length)} place{seedRows.length === 1 ? ' has' : 's have'} a cost but no
            default fee.
          </span>
          <Button
            variant="outline"
            onClick={(): void =>
              setBulk({
                title: 'Seed default fees from our cost',
                request: {
                  source: {
                    kind: 'BULK',
                    action: 'SEED_FROM_COST',
                    label: `Seed ${String(seedRows.length)} default fee(s) from cost`,
                  },
                  defaultEffectiveFrom: props.today,
                  rows: seedRows,
                },
              })
            }
          >
            Seed defaults from our cost
          </Button>
        </div>
      )}
      {bulk !== null && (
        <FeeChangePreview
          title={bulk.title}
          request={bulk.request}
          onDone={(): void => setBulk(null)}
        />
      )}
      {props.sites.length === 0 ? (
        <p style={helpStyle}>
          No disposal places yet. Places appear here once a job goes to one, a cost is entered, or
          you add a drop-off location.
        </p>
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              <SortableHead column="place" sort={sort} onSort={toggle}>
                Place
              </SortableHead>
              <SortableHead column="defaultFee" sort={sort} onSort={toggle}>
                Default fee
              </SortableHead>
              <SortableHead column="customerPrices" sort={sort} onSort={toggle}>
                Customer prices
              </SortableHead>
              {seesCost && (
                <SortableHead column="cost" sort={sort} onSort={toggle}>
                  Our cost
                </SortableHead>
              )}
              {seesCost && (
                <SortableHead column="margin" sort={sort} onSort={toggle}>
                  Margin
                </SortableHead>
              )}
              <TableHead />
            </TableRow>
          </TableHeader>
          <TableBody>
            {sites.map((s) => {
              const cost = s.costHundredthsCentPerBbl ?? null;
              const fee = s.defaultFee?.feeCentsPerBbl ?? null;
              return (
                <TableRow key={`${s.kind}:${s.id}`}>
                  <TableCell>
                    {s.name ?? '—'}
                    {s.kind === 'LOCATION' && <span style={helpStyle}> (your location)</span>}
                    {s.kind === 'WELL' && <span style={helpStyle}> (your disposal well)</span>}
                  </TableCell>
                  <TableCell>
                    {formatFee(fee)}
                    {s.nextDefault !== null && (
                      <div style={helpStyle}>
                        {s.nextDefault.feeCentsPerBbl === null
                          ? 'ends'
                          : formatFee(s.nextDefault.feeCentsPerBbl)}{' '}
                        from {s.nextDefault.effectiveFrom}
                      </div>
                    )}
                  </TableCell>
                  <TableCell>{formatNumber(s.customerPriceCount)}</TableCell>
                  {seesCost && (
                    <TableCell>
                      {cost === null
                        ? '—'
                        : `$${formatNumber(cost / 10000, { decimals: cost % 100 === 0 ? 2 : 4 })}`}
                    </TableCell>
                  )}
                  {seesCost && (
                    <TableCell>
                      {cost === null || fee === null ? '—' : formatSigned(fee - costToCents(cost))}
                    </TableCell>
                  )}
                  <TableCell>
                    <Button
                      size="sm"
                      variant="outline"
                      aria-label={`Open ${s.name ?? 'place'}`}
                      onClick={(): void => setOpen({ kind: s.kind, id: s.id })}
                    >
                      Open
                    </Button>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      )}
      {open !== null && (
        <PlaceDetail
          key={`${open.kind}:${open.id}`}
          place={open}
          today={props.today}
          canEdit={props.canEdit}
          onClose={(): void => setOpen(null)}
        />
      )}
    </>
  );
}

function formatSigned(cents: number): string {
  return cents < 0 ? `−${formatFee(-cents)}` : formatFee(cents);
}

function PlaceDetail(props: {
  readonly place: Place;
  readonly today: string;
  readonly canEdit: boolean;
  readonly onClose: () => void;
}): React.JSX.Element {
  const detail = useFeeSite(props.place);
  const customers = useFeeCustomers();
  const [editing, setEditing] = React.useState<{
    customerId: string | null;
    label: string;
    mode: 'set' | 'end';
  } | null>(null);
  const [forAll, setForAll] = React.useState(false);
  const [bulk, setBulk] = React.useState<PreviewRequest | null>(null);
  const d = detail.data;
  // OPEN-204 — its own URL names, so it sorts apart from the places table above it.
  const { sort, toggle } = useUrlSort(PLACE_PRICE_SORTS, null, [], 'prices');
  const prices = sortRows(d?.customers ?? [], sort, (c, k) => {
    switch (k) {
      case 'customer':
        return c.customerAbbreviation ?? c.customerName;
      case 'fee':
        return c.fee?.feeCentsPerBbl ?? null;
    }
  });

  return (
    <section style={{ ...boxStyle, marginTop: '20px' }} aria-label="Place detail">
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '10px' }}>
        <h2 style={{ fontWeight: 600 }}>{d?.site.name ?? 'Loading…'}</h2>
        <Button variant="ghost" size="sm" onClick={props.onClose}>
          Close
        </Button>
      </div>
      {d !== undefined && (
        <>
          <p style={{ marginBottom: '10px' }}>
            Default fee: <strong>{formatFee(d.defaultFee?.feeCentsPerBbl)}</strong>
            {d.defaultFee !== null && (
              <span style={helpStyle}> since {d.defaultFee.effectiveFrom}</span>
            )}
            {d.nextDefault !== null && (
              <span style={helpStyle}>
                {' '}
                ·{' '}
                {d.nextDefault.feeCentsPerBbl === null
                  ? 'ends'
                  : formatFee(d.nextDefault.feeCentsPerBbl)}{' '}
                from {d.nextDefault.effectiveFrom}
              </span>
            )}
          </p>
          {props.canEdit && editing === null && !forAll && bulk === null && (
            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginBottom: '16px' }}>
              <Button
                variant="outline"
                onClick={(): void =>
                  setEditing({ customerId: null, label: 'Default', mode: 'set' })
                }
              >
                Set default
              </Button>
              {d.defaultFee !== null && (
                <Button
                  variant="outline"
                  onClick={(): void =>
                    setEditing({ customerId: null, label: 'Default', mode: 'end' })
                  }
                >
                  End default
                </Button>
              )}
              <Button variant="outline" onClick={(): void => setForAll(true)}>
                Set for all customers…
              </Button>
            </div>
          )}
          {editing !== null && (
            <FeeForm
              place={props.place}
              placeName={d.site.name}
              customerId={editing.customerId}
              customerLabel={editing.label}
              mode={editing.mode}
              today={props.today}
              onDone={(): void => setEditing(null)}
            />
          )}
          {forAll && (
            <SetForAllForm
              placeName={d.site.name}
              customerPrices={d.customers.filter((c) => c.fee !== null)}
              today={props.today}
              onCancel={(): void => setForAll(false)}
              onPreview={(cents, endFor, day): void => {
                setForAll(false);
                setBulk({
                  source: {
                    kind: 'BULK',
                    action: 'SET_FOR_ALL',
                    label: `${d.site.name}: ${formatFee(cents)} for all customers`,
                  },
                  defaultEffectiveFrom: day,
                  rows: setForAllRows({ site: props.place, cents, endFor }),
                });
              }}
            />
          )}
          {bulk !== null && (
            <FeeChangePreview
              title="Set for all customers"
              request={bulk}
              onDone={(): void => setBulk(null)}
            />
          )}

          <h3 style={{ fontWeight: 600, margin: '12px 0 6px' }}>Customers with their own price</h3>
          {d.customers.length === 0 ? (
            <p style={helpStyle}>None — every customer pays the default here.</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <SortableHead column="customer" sort={sort} onSort={toggle}>
                    Customer
                  </SortableHead>
                  <SortableHead column="fee" sort={sort} onSort={toggle}>
                    Fee
                  </SortableHead>
                  <TableHead>Scheduled</TableHead>
                  {props.canEdit && <TableHead />}
                </TableRow>
              </TableHeader>
              <TableBody>
                {prices.map((c) => {
                  const label = c.customerAbbreviation ?? c.customerName ?? '';
                  return (
                    <TableRow key={c.haulerOperatorId}>
                      <TableCell>
                        {c.customerAbbreviation} <span style={helpStyle}>{c.customerName}</span>
                      </TableCell>
                      <TableCell>
                        {c.fee === null ? 'Default' : formatFee(c.fee.feeCentsPerBbl)}
                      </TableCell>
                      <TableCell>
                        {c.next === null
                          ? '—'
                          : `${c.next.feeCentsPerBbl === null ? 'back to default' : formatFee(c.next.feeCentsPerBbl)} from ${c.next.effectiveFrom}`}
                      </TableCell>
                      {props.canEdit && (
                        <TableCell style={{ whiteSpace: 'nowrap' }}>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={(): void =>
                              setEditing({ customerId: c.haulerOperatorId, label, mode: 'set' })
                            }
                          >
                            Change
                          </Button>{' '}
                          {c.fee !== null && (
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={(): void =>
                                setEditing({ customerId: c.haulerOperatorId, label, mode: 'end' })
                              }
                            >
                              Back to default
                            </Button>
                          )}
                        </TableCell>
                      )}
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
          {props.canEdit && editing === null && customers.data !== undefined && (
            <AddCustomerPrice
              customers={customers.data.customers.filter(
                (c) => !d.customers.some((x) => x.haulerOperatorId === c.id),
              )}
              onPick={(c): void =>
                setEditing({ customerId: c.id, label: c.abbreviation, mode: 'set' })
              }
            />
          )}
          <History filter={{ kind: props.place.kind, id: props.place.id }} />
        </>
      )}
    </section>
  );
}

function AddCustomerPrice(props: {
  readonly customers: Customer[];
  readonly onPick: (c: Customer) => void;
}): React.JSX.Element | null {
  const [id, setId] = React.useState('');
  if (props.customers.length === 0) return null;
  return (
    <div style={{ display: 'flex', gap: '8px', alignItems: 'center', margin: '12px 0' }}>
      <label htmlFor="add-customer-price" style={helpStyle}>
        Give a customer its own price:
      </label>
      <select
        id="add-customer-price"
        value={id}
        onChange={(e): void => setId(e.target.value)}
        style={selectStyle}
      >
        <option value="">Choose a customer…</option>
        {props.customers.map((c) => (
          <option key={c.id} value={c.id}>
            {c.abbreviation} {c.name !== null ? `— ${c.name}` : ''}
          </option>
        ))}
      </select>
      <Button
        variant="outline"
        disabled={id === ''}
        onClick={(): void => {
          const c = props.customers.find((x) => x.id === id);
          if (c !== undefined) props.onPick(c);
        }}
      >
        Set price
      </Button>
    </div>
  );
}

function SetForAllForm(props: {
  readonly placeName: string;
  readonly customerPrices: { haulerOperatorId: string; customerAbbreviation: string | null }[];
  readonly today: string;
  readonly onCancel: () => void;
  readonly onPreview: (cents: number, endFor: string[], day: string) => void;
}): React.JSX.Element {
  const [fee, setFee] = React.useState('');
  const [day, setDay] = React.useState(props.today);
  const [ending, setEnding] = React.useState<ReadonlySet<string>>(new Set());
  const read = readFeeInput(fee);
  return (
    <div style={boxStyle}>
      <h3 style={{ fontWeight: 600, marginBottom: '8px' }}>
        Set {props.placeName} for all customers
      </h3>
      <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', marginBottom: '10px' }}>
        <div>
          <label htmlFor="all-fee" style={labelStyle}>
            New default ($/bbl)
          </label>
          <Input id="all-fee" value={fee} onChange={(e): void => setFee(e.target.value)} />
        </div>
        <div>
          <label htmlFor="all-day" style={labelStyle}>
            Starting
          </label>
          <Input
            id="all-day"
            type="date"
            value={day}
            onChange={(e): void => setDay(e.target.value)}
          />
        </div>
      </div>
      {props.customerPrices.length > 0 && (
        <fieldset style={{ marginBottom: '10px' }}>
          <legend style={helpStyle}>
            These customers have their own price here. Tick any that should go to the new default:
          </legend>
          {props.customerPrices.map((c) => {
            const abbr = c.customerAbbreviation ?? '';
            return (
              <label key={c.haulerOperatorId} style={{ display: 'flex', gap: '6px' }}>
                <input
                  type="checkbox"
                  checked={ending.has(abbr)}
                  onChange={(e): void => {
                    const next = new Set(ending);
                    if (e.target.checked) next.add(abbr);
                    else next.delete(abbr);
                    setEnding(next);
                  }}
                />
                {abbr}
              </label>
            );
          })}
        </fieldset>
      )}
      {fee !== '' && !read.ok && <p style={{ color: 'var(--color-danger)' }}>{read.message}</p>}
      <div style={{ display: 'flex', gap: '8px' }}>
        <Button
          disabled={!read.ok || day === ''}
          onClick={(): void => {
            if (read.ok) props.onPreview(read.cents, [...ending], day);
          }}
        >
          Preview
        </Button>
        <Button variant="outline" onClick={props.onCancel}>
          Cancel
        </Button>
      </div>
    </div>
  );
}

// ── By customer ────────────────────────────────────────────────────────────────────

function ByCustomer(props: {
  readonly today: string;
  readonly canEdit: boolean;
  /** Deep link: open on this customer… */
  readonly initialCustomerId?: string | null;
  /** …with the fee form for this place already open (needs canEdit). */
  readonly initialPlace?: Place | null;
}): React.JSX.Element {
  const customers = useFeeCustomers();
  const [customerId, setCustomerId] = React.useState<string | null>(
    props.initialCustomerId ?? null,
  );
  const fees = useCustomerFees(customerId);
  const [editing, setEditing] = React.useState<{
    place: Place;
    name: string;
    mode: 'set' | 'end';
  } | null>(null);
  // Open the linked place's fee form once, when this customer's places have loaded.
  const pendingPlace = React.useRef(props.initialPlace ?? null);
  React.useEffect(() => {
    const want = pendingPlace.current;
    if (want === null || fees.data === undefined || !props.canEdit) return;
    pendingPlace.current = null;
    const site = fees.data.sites.find((s) => s.kind === want.kind && s.id === want.id);
    setEditing({ place: want, name: site?.name ?? '', mode: 'set' });
  }, [fees.data, props.canEdit]);
  const [panel, setPanel] = React.useState<'none' | 'everywhere' | 'copy'>('none');
  const [bulk, setBulk] = React.useState<{ title: string; request: PreviewRequest } | null>(null);
  const list = customers.data?.customers ?? [];
  const current = list.find((c) => c.id === customerId);
  // OPEN-204 — its own URL names (this tab is not the main table).
  const { sort, toggle } = useUrlSort(CUSTOMER_FEE_SORTS, null, [], 'fees');
  const feeSites = sortRows(fees.data?.sites ?? [], sort, (s, k) => {
    switch (k) {
      case 'place':
        return s.name;
      case 'fee':
        return s.feeCentsPerBbl;
      case 'source':
        return SOURCE_LABELS[s.source];
      case 'since':
        return s.effectiveFrom;
    }
  });

  return (
    <>
      <label htmlFor="fee-customer" style={labelStyle}>
        Customer
      </label>
      <select
        id="fee-customer"
        value={customerId ?? ''}
        onChange={(e): void => {
          setCustomerId(e.target.value === '' ? null : e.target.value);
          setEditing(null);
          setPanel('none');
          setBulk(null);
        }}
        style={{ ...selectStyle, marginBottom: '16px' }}
      >
        <option value="">Choose a customer…</option>
        {list.map((c) => (
          <option key={c.id} value={c.id}>
            {c.abbreviation} {c.name !== null ? `— ${c.name}` : ''}
          </option>
        ))}
      </select>
      {current !== undefined && (
        // Gap 1: the customer's page has its job-type prices too.
        <div style={{ marginBottom: '16px' }}>
          <Link to={`/customers/${current.id}`} style={{ color: 'var(--color-link)' }}>
            All prices for this customer →
          </Link>
        </div>
      )}

      {current !== undefined && fees.data !== undefined && (
        <>
          {props.canEdit && panel === 'none' && bulk === null && editing === null && (
            <div style={{ display: 'flex', gap: '8px', marginBottom: '16px' }}>
              <Button variant="outline" onClick={(): void => setPanel('everywhere')}>
                One fee at every place…
              </Button>
              <Button variant="outline" onClick={(): void => setPanel('copy')}>
                Copy prices from another customer…
              </Button>
            </div>
          )}
          {panel === 'everywhere' && (
            <OneFeeForm
              title={`One fee for ${current.abbreviation} at every place`}
              today={props.today}
              onCancel={(): void => setPanel('none')}
              onPreview={(cents, day): void => {
                setPanel('none');
                setBulk({
                  title: `One fee for ${current.abbreviation}`,
                  request: {
                    source: {
                      kind: 'BULK',
                      action: 'SET_FOR_ALL',
                      label: `${current.abbreviation}: ${formatFee(cents)} at every place`,
                    },
                    defaultEffectiveFrom: day,
                    rows: oneFeeEverywhereRows({
                      abbreviation: current.abbreviation,
                      sites: fees.data?.sites ?? [],
                      cents,
                    }),
                  },
                });
              }}
            />
          )}
          {panel === 'copy' && (
            <CopyFromForm
              to={current}
              customers={list.filter((c) => c.id !== current.id)}
              today={props.today}
              onCancel={(): void => setPanel('none')}
              onPreview={(request): void => {
                setPanel('none');
                setBulk({ title: 'Copy prices', request });
              }}
            />
          )}
          {bulk !== null && (
            <FeeChangePreview
              title={bulk.title}
              request={bulk.request}
              onDone={(): void => setBulk(null)}
            />
          )}
          {editing !== null && (
            <FeeForm
              place={editing.place}
              placeName={editing.name}
              customerId={current.id}
              customerLabel={current.abbreviation}
              mode={editing.mode}
              today={props.today}
              onDone={(): void => setEditing(null)}
            />
          )}
          <Table>
            <TableHeader>
              <TableRow>
                <SortableHead column="place" sort={sort} onSort={toggle}>
                  Place
                </SortableHead>
                <SortableHead column="fee" sort={sort} onSort={toggle}>
                  Fee
                </SortableHead>
                <SortableHead column="source" sort={sort} onSort={toggle}>
                  From
                </SortableHead>
                <SortableHead column="since" sort={sort} onSort={toggle}>
                  Since
                </SortableHead>
                {props.canEdit && <TableHead />}
              </TableRow>
            </TableHeader>
            <TableBody>
              {feeSites.map((s) => (
                <TableRow key={`${s.kind}:${s.id}`}>
                  <TableCell>
                    {s.name ?? '—'}
                    {s.kind === 'LOCATION' && <span style={helpStyle}> (your location)</span>}
                    {s.kind === 'WELL' && <span style={helpStyle}> (your disposal well)</span>}
                    {s.excluded && (
                      <div style={{ ...helpStyle, color: 'var(--color-warning)' }}>
                        Excluded — this customer&apos;s water is never sent here
                      </div>
                    )}
                  </TableCell>
                  <TableCell>{formatFee(s.feeCentsPerBbl)}</TableCell>
                  <TableCell>
                    <span
                      style={{
                        fontWeight: s.source === 'CUSTOMER' ? 600 : 400,
                        color: s.source === 'NONE' ? 'var(--color-text-secondary)' : undefined,
                      }}
                    >
                      {SOURCE_LABELS[s.source]}
                    </span>
                  </TableCell>
                  <TableCell>{s.effectiveFrom ?? '—'}</TableCell>
                  {props.canEdit && (
                    <TableCell style={{ whiteSpace: 'nowrap' }}>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={(): void =>
                          setEditing({
                            place: { kind: s.kind, id: s.id },
                            name: s.name ?? '',
                            mode: 'set',
                          })
                        }
                      >
                        Set price
                      </Button>{' '}
                      {s.source === 'CUSTOMER' && (
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={(): void =>
                            setEditing({
                              place: { kind: s.kind, id: s.id },
                              name: s.name ?? '',
                              mode: 'end',
                            })
                          }
                        >
                          Back to default
                        </Button>
                      )}
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <History filter={{ haulerOperatorId: current.id }} />
        </>
      )}
    </>
  );
}

function OneFeeForm(props: {
  readonly title: string;
  readonly today: string;
  readonly onCancel: () => void;
  readonly onPreview: (cents: number, day: string) => void;
}): React.JSX.Element {
  const [fee, setFee] = React.useState('');
  const [day, setDay] = React.useState(props.today);
  const read = readFeeInput(fee);
  return (
    <div style={boxStyle}>
      <h3 style={{ fontWeight: 600, marginBottom: '8px' }}>{props.title}</h3>
      <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', marginBottom: '10px' }}>
        <div>
          <label htmlFor="one-fee" style={labelStyle}>
            Fee ($/bbl)
          </label>
          <Input id="one-fee" value={fee} onChange={(e): void => setFee(e.target.value)} />
        </div>
        <div>
          <label htmlFor="one-day" style={labelStyle}>
            Starting
          </label>
          <Input
            id="one-day"
            type="date"
            value={day}
            onChange={(e): void => setDay(e.target.value)}
          />
        </div>
      </div>
      {fee !== '' && !read.ok && <p style={{ color: 'var(--color-danger)' }}>{read.message}</p>}
      <div style={{ display: 'flex', gap: '8px' }}>
        <Button
          disabled={!read.ok || day === ''}
          onClick={(): void => {
            if (read.ok) props.onPreview(read.cents, day);
          }}
        >
          Preview
        </Button>
        <Button variant="outline" onClick={props.onCancel}>
          Cancel
        </Button>
      </div>
    </div>
  );
}

function CopyFromForm(props: {
  readonly to: Customer;
  readonly customers: Customer[];
  readonly today: string;
  readonly onCancel: () => void;
  readonly onPreview: (r: PreviewRequest) => void;
}): React.JSX.Element {
  const [fromId, setFromId] = React.useState('');
  const [day, setDay] = React.useState(props.today);
  const from = useCustomerFees(fromId === '' ? null : fromId);
  const fromCustomer = props.customers.find((c) => c.id === fromId);
  const rows =
    from.data === undefined
      ? []
      : copyCustomerRows({ fromSites: from.data.sites, toAbbreviation: props.to.abbreviation });
  return (
    <div style={boxStyle}>
      <h3 style={{ fontWeight: 600, marginBottom: '8px' }}>
        Copy prices to {props.to.abbreviation}
      </h3>
      <p style={{ ...helpStyle, marginBottom: '10px' }}>
        Copies the other customer&apos;s own prices. Places where they pay the default are left
        alone.
      </p>
      <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', marginBottom: '10px' }}>
        <div>
          <label htmlFor="copy-from" style={labelStyle}>
            Copy from
          </label>
          <select
            id="copy-from"
            value={fromId}
            onChange={(e): void => setFromId(e.target.value)}
            style={selectStyle}
          >
            <option value="">Choose a customer…</option>
            {props.customers.map((c) => (
              <option key={c.id} value={c.id}>
                {c.abbreviation}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label htmlFor="copy-day" style={labelStyle}>
            Starting
          </label>
          <Input
            id="copy-day"
            type="date"
            value={day}
            onChange={(e): void => setDay(e.target.value)}
          />
        </div>
      </div>
      {from.data !== undefined && rows.length === 0 && (
        <p style={helpStyle}>{fromCustomer?.abbreviation} has no prices of its own to copy.</p>
      )}
      <div style={{ display: 'flex', gap: '8px' }}>
        <Button
          disabled={rows.length === 0 || day === '' || fromCustomer === undefined}
          onClick={(): void =>
            props.onPreview({
              source: {
                kind: 'BULK',
                action: 'COPY_CUSTOMER',
                label: `Copy ${fromCustomer?.abbreviation ?? ''} prices to ${props.to.abbreviation}`,
              },
              defaultEffectiveFrom: day,
              rows,
            })
          }
        >
          Preview
        </Button>
        <Button variant="outline" onClick={props.onCancel}>
          Cancel
        </Button>
      </div>
    </div>
  );
}

// ── Export & import ────────────────────────────────────────────────────────────────

function ExportImport(props: {
  readonly today: string;
  readonly canEdit: boolean;
}): React.JSX.Element {
  const opts = useApiOpts();
  const [every, setEvery] = React.useState(false);
  const [busy, setBusy] = React.useState(false);
  const [day, setDay] = React.useState(props.today);
  const [uploadError, setUploadError] = React.useState<string | null>(null);
  const [request, setRequest] = React.useState<PreviewRequest | null>(null);
  const fileRef = React.useRef<HTMLInputElement>(null);

  const download = async (format: 'csv' | 'xlsx'): Promise<void> => {
    setBusy(true);
    try {
      await downloadFees(opts, { format, every });
    } catch (e) {
      toast.error(apiErrorText(e, 'Export failed.'));
    } finally {
      setBusy(false);
    }
  };

  const upload = async (file: File): Promise<void> => {
    setUploadError(null);
    setRequest(null);
    setBusy(true);
    try {
      const parsed = await parseFeeFile(opts, file);
      setRequest({
        source: { kind: 'FILE', fileName: parsed.fileName, fileSha256: parsed.fileSha256 },
        defaultEffectiveFrom: day,
        rows: parsed.rows,
      });
    } catch (e) {
      setUploadError(apiErrorText(e, 'Upload failed.'));
    } finally {
      setBusy(false);
      if (fileRef.current !== null) fileRef.current.value = '';
    }
  };

  return (
    <>
      <section style={boxStyle}>
        <h2 style={{ fontWeight: 600, marginBottom: '6px' }}>Export</h2>
        <p style={{ ...helpStyle, marginBottom: '10px' }}>
          One row per place default and per customer price. Edit the &quot;New Fee&quot; column and
          upload it back below. Leave a New Fee blank to leave that price alone; type DEFAULT to
          send a customer back to the default.
        </p>
        <label style={{ ...helpStyle, display: 'flex', gap: '6px', marginBottom: '10px' }}>
          <input
            type="checkbox"
            checked={every}
            onChange={(e): void => setEvery(e.target.checked)}
          />
          Every customer at every place (a sheet to fill in)
        </label>
        <div style={{ display: 'flex', gap: '8px' }}>
          <Button variant="outline" disabled={busy} onClick={(): void => void download('xlsx')}>
            Excel
          </Button>
          <Button variant="outline" disabled={busy} onClick={(): void => void download('csv')}>
            CSV
          </Button>
        </div>
      </section>
      {props.canEdit && (
        <section style={boxStyle}>
          <h2 style={{ fontWeight: 600, marginBottom: '6px' }}>Import</h2>
          <p style={{ ...helpStyle, marginBottom: '10px' }}>
            You will see every change before anything is saved. If any row has a problem, nothing is
            saved.
          </p>
          <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', alignItems: 'flex-end' }}>
            <div>
              <label htmlFor="import-day" style={labelStyle}>
                Start date for rows without one
              </label>
              <Input
                id="import-day"
                type="date"
                value={day}
                onChange={(e): void => setDay(e.target.value)}
              />
            </div>
            <div>
              <label htmlFor="import-file" style={labelStyle}>
                File (CSV or Excel)
              </label>
              <input
                id="import-file"
                ref={fileRef}
                type="file"
                accept=".csv,.xlsx"
                disabled={busy || day === ''}
                onChange={(e): void => {
                  const f = e.target.files?.[0];
                  if (f !== undefined) void upload(f);
                }}
              />
            </div>
          </div>
          {uploadError !== null && (
            <p role="alert" style={{ color: 'var(--color-danger)', marginTop: '10px' }}>
              {uploadError}
            </p>
          )}
        </section>
      )}
      {request !== null && (
        <FeeChangePreview
          title={`Import ${request.source.kind === 'FILE' ? request.source.fileName : ''}`}
          request={request}
          onDone={(): void => setRequest(null)}
        />
      )}
    </>
  );
}

// ── Shared ─────────────────────────────────────────────────────────────────────────

function FeeForm(props: {
  readonly place: Place;
  readonly placeName: string;
  readonly customerId: string | null;
  readonly customerLabel: string;
  readonly mode: 'set' | 'end';
  readonly today: string;
  readonly onDone: () => void;
}): React.JSX.Element {
  const [fee, setFee] = React.useState('');
  const [day, setDay] = React.useState(props.today);
  const [notes, setNotes] = React.useState('');
  const save = useSetFee(() => {
    toast.success(props.mode === 'end' ? 'Price ended.' : 'Price saved.');
    props.onDone();
  });
  const read = readFeeInput(fee);
  const who = props.customerId === null ? 'the default' : props.customerLabel;
  const ok = (props.mode === 'end' || read.ok) && day !== '';

  return (
    <form
      style={boxStyle}
      aria-label="Fee form"
      onSubmit={(e): void => {
        e.preventDefault();
        if (!ok) return;
        save.mutate({
          target: props.place,
          haulerOperatorId: props.customerId,
          feeCentsPerBbl: props.mode === 'end' || !read.ok ? null : read.cents,
          effectiveFrom: day,
          notes: notes.trim() === '' ? null : notes,
        });
      }}
    >
      <h3 style={{ fontWeight: 600, marginBottom: '8px' }}>
        {props.mode === 'end'
          ? props.customerId === null
            ? `End the default at ${props.placeName}`
            : `Send ${who} back to the default at ${props.placeName}`
          : `${props.placeName}: price for ${who}`}
      </h3>
      <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', marginBottom: '10px' }}>
        {props.mode === 'set' && (
          <div>
            <label htmlFor="fee-amount" style={labelStyle}>
              Fee ($/bbl)
            </label>
            <Input
              id="fee-amount"
              inputMode="decimal"
              value={fee}
              onChange={(e): void => setFee(e.target.value)}
            />
          </div>
        )}
        <div>
          <label htmlFor="fee-day" style={labelStyle}>
            Starting
          </label>
          <Input
            id="fee-day"
            type="date"
            value={day}
            onChange={(e): void => setDay(e.target.value)}
          />
        </div>
        <div style={{ flex: 1, minWidth: '200px' }}>
          <label htmlFor="fee-notes" style={labelStyle}>
            Note (optional)
          </label>
          <Input
            className="keep-case"
            id="fee-notes"
            value={notes}
            onChange={(e): void => setNotes(e.target.value)}
          />
        </div>
      </div>
      {day !== '' && day < props.today && (
        <p style={{ ...helpStyle, color: 'var(--color-warning)' }}>
          This starts in the past. Tickets already approved keep the fee they had.
        </p>
      )}
      {props.mode === 'set' && fee !== '' && !read.ok && (
        <p style={{ color: 'var(--color-danger)' }}>{read.message}</p>
      )}
      {save.isError && (
        <p role="alert" style={{ color: 'var(--color-danger)' }}>
          {apiErrorText(save.error, 'Could not save the fees. Try again.')}
        </p>
      )}
      <div style={{ display: 'flex', gap: '8px' }}>
        <Button type="submit" disabled={!ok || save.isPending}>
          {props.mode === 'end' ? 'End price' : 'Save'}
        </Button>
        <Button type="button" variant="outline" onClick={props.onDone}>
          Cancel
        </Button>
      </div>
    </form>
  );
}

function History(props: { readonly filter: HistoryFilter }): React.JSX.Element {
  const [open, setOpen] = React.useState(false);
  const history = useFeeHistory(open ? props.filter : null);
  return (
    <div style={{ marginTop: '16px' }}>
      <Button variant="ghost" size="sm" onClick={(): void => setOpen(!open)} aria-expanded={open}>
        {open ? 'Hide history' : 'Show history'}
      </Button>
      {open && history.data !== undefined && (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Entered</TableHead>
              <TableHead>By</TableHead>
              <TableHead>Customer</TableHead>
              <TableHead>Place</TableHead>
              <TableHead>Fee</TableHead>
              <TableHead>From</TableHead>
              <TableHead>Note</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {history.data.rows.map((h) => (
              <TableRow key={h.id}>
                <TableCell>{new Date(h.createdAt).toLocaleString()}</TableCell>
                <TableCell>{h.createdByName ?? '—'}</TableCell>
                <TableCell>
                  {h.haulerOperatorId === null ? 'Default' : (h.customerName ?? '—')}
                </TableCell>
                <TableCell>{h.targetName ?? '—'}</TableCell>
                <TableCell>
                  {h.feeCentsPerBbl === null ? 'Ended' : formatFee(h.feeCentsPerBbl)}
                </TableCell>
                <TableCell>{h.effectiveFrom}</TableCell>
                <TableCell>
                  {h.notes}
                  {h.importId !== null && <span style={helpStyle}> (bulk change)</span>}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
      {open && history.data?.truncated === true && (
        <p style={helpStyle}>Showing the newest 1,000 changes.</p>
      )}
    </div>
  );
}
